import { isEqual } from 'lodash';
import { HTMLAttributes, memo, useCallback } from 'react';

import { EntityDetailsProvider } from '@/modules/entities/contexts/entityDetails/EntityDetails.provider';
import { ClientProfileSummaryPanel } from '@/modules/summaryPanels/ClientProfileSummaryPanel/ClientProfileSummaryPanel';
import { ClientProfileTabs } from '@/modules/summaryPanels/ClientProfileSummaryPanel/ClientProfileSummaryPanel.types';
import { EntitySummaryPanel } from '@/modules/summaryPanels/EntitySummaryPanel/EntitySummaryPanel';
import { OrganizationSummaryPanel } from '@/modules/summaryPanels/OrganizationSummaryPanel/OrganizationSummaryPanel';
import { ClientOrganizationTabs } from '@/modules/summaryPanels/OrganizationSummaryPanel/OrganizationSummaryPanel.types';
import { TestamentaryEntitySummaryPanel } from '@/modules/summaryPanels/TestamentaryEntitySummaryPanel/TestamentaryEntitySummaryPanel';
import { TestamentaryEntityTabs } from '@/modules/summaryPanels/TestamentaryEntitySummaryPanel/TestamentaryEntitySummaryPanel.types';
import { TILE_NOTES_BULK_PANEL_ID } from '@/modules/summaryPanels/TileNotesSummaryPanel/constants';
import { EntityTileNotesSummaryPanel } from '@/modules/summaryPanels/TileNotesSummaryPanel/EntityTileNotesSummaryPanel';

import { useEntityDiagramContext } from '../../contexts/entityDiagram.context';

export interface NodeSummaryPanelProps extends HTMLAttributes<HTMLDivElement> {
  householdId: string;
  isOnEntityPage?: boolean;
}

const SummaryComponent = ({
  householdId,
  isOnEntityPage,
}: NodeSummaryPanelProps) => {
  const { state, dispatch } = useEntityDiagramContext();
  const summaryNode = state.derivedData.summaryNode;

  const onClose = useCallback(() => {
    dispatch({ type: 'CLOSE_SUMMARY_PANEL' });
  }, [dispatch]);

  if (state.summaryNodeId === TILE_NOTES_BULK_PANEL_ID) {
    return (
      <EntityTileNotesSummaryPanel
        nodes={state.nodes}
        dispatch={(action) =>
          dispatch({
            ...action,
            isInteractive: false,
          })
        }
        onApplyNotes={(updates) =>
          dispatch({ type: 'APPLY_TILE_NOTES', updates })
        }
        onClose={onClose}
      />
    );
  }

  const data = summaryNode?.data;

  switch (data?.node?.__typename) {
    case 'ClientProfile': {
      return (
        <EntityDetailsProvider>
          <ClientProfileSummaryPanel
            householdId={householdId}
            clientProfileId={data.id}
            onClose={onClose}
            afterDeath={data.afterDeath}
            isOnSourceWaterfall={false}
            isOnHypotheticalWaterfall={false}
            key={data.id}
            initialTabOverride={ClientProfileTabs.Disposition}
          />
        </EntityDetailsProvider>
      );
    }
    case 'ClientOrganization': {
      return (
        <EntityDetailsProvider>
          <OrganizationSummaryPanel
            householdId={householdId}
            organizationId={data.id}
            onClose={onClose}
            isOnSourceWaterfall={false}
            isOnHypotheticalWaterfall={false}
            isOnSingleEntityDiagram={true}
            initialTabOverride={ClientOrganizationTabs.Disposition}
          />
        </EntityDetailsProvider>
      );
    }
    case 'Entity': {
      return (
        <EntityDetailsProvider>
          <EntitySummaryPanel
            householdId={householdId}
            entityId={data.id}
            onClose={onClose}
            isOnSourceWaterfall={false}
            isOnHypotheticalWaterfall={false}
            isHypotheticalEntity={false}
            key={data.id}
            showGoToEntityButton={false}
            isOnEntityPage={isOnEntityPage}
            isOnSingleEntityDiagram={true}
          />
        </EntityDetailsProvider>
      );
    }
    case 'TestamentaryEntity': {
      const firstPrimaryClientDeathId =
        state.entityDiagramGraph.getAttributes().firstPrimaryClientDeathId;

      return (
        <EntityDetailsProvider>
          <TestamentaryEntitySummaryPanel
            householdId={householdId}
            entityId={data.id}
            onClose={onClose}
            firstPrimaryClientDeathId={firstPrimaryClientDeathId}
            isOnWaterfall={false}
            initialTabOverride={TestamentaryEntityTabs.Disposition}
          />
        </EntityDetailsProvider>
      );
    }
    default:
      return null;
  }
};

export const MemoizedNodeSummaryPanel = memo(function NodeSummaryPanel({
  householdId,
  isOnEntityPage,
  ...props
}: NodeSummaryPanelProps) {
  const { state } = useEntityDiagramContext();
  const isBulkNotesPanel = state.summaryNodeId === TILE_NOTES_BULK_PANEL_ID;
  if (!state.derivedData.summaryNode && !isBulkNotesPanel) return null;

  return (
    <div {...props}>
      <SummaryComponent
        householdId={householdId}
        isOnEntityPage={isOnEntityPage}
      />
    </div>
  );
}, isEqual);
